import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { ScheduleAdvancedMultiSelectComponent } from 'src/app/common-components/entity-select/schedule/multi-select/schedule-advanced-multi-select.component';
import { closedExceptionItemDialogSubmit } from 'src/app/pages/schedule-details/state/schedule-detail.actions';
import { AddClosedExceptionComponent } from '../add-closed-exception/containers/add-closed-exception.component';
import { toUtcMidnight } from './helpers/utc-date-helper';
import { ClosedExceptionItemView } from './models/closed-exceptions.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-closed-exceptions',
    styleUrls: ['./closed-exceptions.component.scss'],
    templateUrl: './closed-exceptions.component.html',
    standalone: false
})
export class ClosedExceptionsComponent implements ControlValueAccessor, OnDestroy, OnInit {
   @Input() callState: CallState;

   @Input() canEdit: boolean;

   @Input() canDelete: boolean;

   @Input() accountId: number;

   @Input() scheduleId: number | null;

   @Output() addClosedExceptionClick = new EventEmitter<void>();

   @Output() editClosedExceptionClick = new EventEmitter<number>();

   columns: string[] = ['StartDate', 'EndDate', 'StartTime', 'EndTime', 'Prompt', 'MoreActions'];

   closedExceptionItems: ClosedExceptionItemView[] = [];

   disabled = false;

   isEditing: boolean = false;

   onTouched: () => void;

   private _showPastClosedExceptions = false;

   get showPastClosedExceptions(): boolean {
      return this._showPastClosedExceptions;
   }

   set showPastClosedExceptions(value: boolean) {
      this._showPastClosedExceptions = value;
      this.refilterClosedExceptions();
   }

   private originalClosedExceptionItems: ClosedExceptionItemView[] = [];

   private destroyed$ = new Subject<void>();

   private onChange: (value: Array<ClosedExceptionItemView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      private dialogService: LcsDialogService
   ) {
      control.valueAccessor = this;
      this.actions$.pipe(ofType(closedExceptionItemDialogSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         const closedExceptionToAdd: ClosedExceptionItemView = {
            closedExceptionId: 0,
            endDate: toUtcMidnight(action.closedExceptionItem.endDate),
            endTime: action.closedExceptionItem.endTime
               ? this.convertTimeStringToDate(action.closedExceptionItem.endTime)
               : null,
            isDeleted: false,
            prompt: action.closedExceptionItem.prompt,
            startDate: toUtcMidnight(action.closedExceptionItem.startDate),
            startTime: action.closedExceptionItem.startTime
               ? this.convertTimeStringToDate(action.closedExceptionItem.startTime)
               : null,
            schedulesToCopyTo: action.closedExceptionItem.schedulesToCopyTo?.map((ce) => ce.scheduleId) ?? [],
         };
         this.closedExceptionItems = [...this.closedExceptionItems];
         this.closedExceptionItems.push(closedExceptionToAdd);
         this.onChange(this.closedExceptionItems);
         this.changeDetectorRef.markForCheck();
      });
   }

   ngOnInit(): void {
      this.refilterClosedExceptions();
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this Closed Exception?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDeleteClick(index: number): void {
      this.deleteConfirmationDialog()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((confirmed) => {
            if (confirmed) {
               this.closedExceptionItems = [...this.closedExceptionItems];
               this.closedExceptionItems[index].isDeleted = true;
               this.changeDetectorRef.markForCheck();
               this.onChange(this.closedExceptionItems);
            }
         });
   }

   writeValue(closedExceptionItems: ClosedExceptionItemView[]): void {
      if (closedExceptionItems?.length > 0) {
         this.originalClosedExceptionItems = [...closedExceptionItems].sort((a, b) => {
            if (a.startDate !== null && b.startDate !== null && a.startDate !== b.startDate) {
               return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
            } else if (a.endDate !== null && b.endDate !== null) {
               return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
            }
            return 0;
         });
      } else {
         this.originalClosedExceptionItems = [];
      }
      this.refilterClosedExceptions();
   }

   registerOnChange(onChange: (value: Array<ClosedExceptionItemView> | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   onEditClosedExceptionClick(index: number): void {
      const dialogRef = this.dialogService.openFormComponent(AddClosedExceptionComponent, {
         closeOnNavigation: true,
         data: {
            accountId: this.accountId,
            closedExceptionFormData: this.closedExceptionItems[index],
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      dialogRef
         .afterClosed()
         .pipe(
            filter((closedExceptionItem) => {
               return closedExceptionItem !== undefined;
            })
         )
         .subscribe((closedExceptionItem) => {
            closedExceptionItem.startDate = toUtcMidnight(closedExceptionItem.startDate);
            closedExceptionItem.endDate = toUtcMidnight(closedExceptionItem.endDate);
            closedExceptionItem.startTime = closedExceptionItem.startTime
               ? this.convertTimeStringToDate(closedExceptionItem.startTime)
               : null;
            closedExceptionItem.endTime = closedExceptionItem.endTime
               ? this.convertTimeStringToDate(closedExceptionItem.endTime)
               : null;
            closedExceptionItem.accountId = this.accountId;
            closedExceptionItem.isDeleted = false;
            this.closedExceptionItems = [...this.closedExceptionItems];
            this.closedExceptionItems[index] = closedExceptionItem;
            this.onChange(this.closedExceptionItems);
            this.changeDetectorRef.markForCheck();
         });
   }

   onCopyClosedExceptionClick(index: number): void {
      this.isEditing = true;
      const dialogRef = this.dialogService.open(ScheduleAdvancedMultiSelectComponent, {
         closeOnNavigation: true,
         data: {
            accountIdFilter: this.accountId,
            scheduleIdsToExclude: this.scheduleId,
            isEditing: this.isEditing,
         },
         disableClose: false,
         panelClass: [DialogSize.Medium, 'dialog-container'],
      });
      dialogRef
         .afterClosed()
         .pipe()
         .subscribe((selectedSchedules) => {
            if (selectedSchedules) {
               this.closedExceptionItems[index].schedulesToCopyTo = selectedSchedules.map(
                  (schedule) => schedule.scheduleId
               );
               this.onChange(this.closedExceptionItems);
               this.changeDetectorRef.markForCheck();
            }
         });
   }

   onAddClosedExceptionClick() {
      this.addClosedExceptionClick.emit();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   private refilterClosedExceptions(): void {
      let filteredItems = [...this.originalClosedExceptionItems];
      const now = new Date();

      if (!this._showPastClosedExceptions && filteredItems?.length > 0) {
         filteredItems = filteredItems.filter((item) => {
            if (!item.endDate) {
               return false;
            }

            const endDate = new Date(item.endDate);
            const endDateUTC = new Date(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());
            const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            if (endDateUTC > currentDate) {
               return true;
            }

            if (endDateUTC.getTime() === currentDate.getTime()) {
               if (item.endTime) {
                  const endTime = new Date(item.endTime);
                  const currentTime = now.getHours() * 60 + now.getMinutes();
                  const endTimeMinutes = endTime.getUTCHours() * 60 + endTime.getUTCMinutes();
                  return currentTime <= endTimeMinutes;
               }
               return true;
            }
            return false;
         });
      }

      this.closedExceptionItems = filteredItems.map((item) => {
         return { ...item, isDeleted: item.isDeleted };
      });

      this.changeDetectorRef.markForCheck();
   }

   private convertTimeStringToDate(timeString: string): Date {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date(Date.UTC(1970, 0, 1, hours, minutes, 0));
      return date;
   }
}
